/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { isNil, truncate } from 'lodash';

import { parseBasicUrls } from 'utils/url';
import { getDisplayDateAndTime, isLessThanNMinutesOld } from 'utils/date';
import { getEntityFromProfile } from 'utils/view';
import {
  MESSAGE_TYPE_BRAND,
  MESSAGE_TYPE_INFLUENCER,
  getAttachmentIcon,
  isTempMessage
} from 'utils/messages';
import { t } from 'utils/language';
import { insertIf } from 'utils/list';

import Spinner from 'components/spinner/Spinner';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import SVGIcon from 'components/svg-icon/SVGIcon';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import MediaPreview from 'components/MediaPreview';
import MessageSeen from './MessageSeen';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import { deleteMessage } from 'modules/messenger/reducers/messages';

import type { MessengerMessage } from 'types';

const MAX_SENDER_NAME_LENGTH = 40;

type OwnProps = {
  subcampaignId: number,
  message: MessengerMessage,
  isBrand: boolean
};

type StateProps = {
  meta: any,
  conversationExternalId: string,
  currentAccountUUID: string | undefined | null
};

type DispatchProps = {
  dispatchDeleteMessage: (c: number, b: string, a: MessengerMessage) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isHovered: boolean
};

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

class UserMessage extends React.PureComponent<Props, State> {
  state = {
    isHovered: false
  };

  getSender() {
    const { message, meta } = this.props;

    return meta.users[message.senderId];
  }

  getEntity() {
    const { message, meta } = this.props;
    const sender = this.getSender();
    const messageProfile = message?.metadata?.profile;
    const { organizations } = meta;

    if (!sender) {
      return null;
    }

    const senderOrganizations = (sender.organizations || []).map(orgId => organizations[orgId]);
    let senderProfile = null;

    if (messageProfile) {
      // it's possible to be a part of both brand and influencer organizations and still chat in Messenger
      const organization = senderOrganizations.find((o) => {
        if (o) {
          const profile = o[`${messageProfile.type}Profile`];

          return profile && profile.id === messageProfile.id;
        }

        return false;
      });

      if (organization) {
        senderProfile = organization[`${messageProfile.type}Profile`];
      }
    }

    return senderProfile ? getEntityFromProfile(senderProfile) : null;
  }

  handleMouseEnter = () => {
    this.setState({
      isHovered: true
    });
  };

  handleMouseLeave = () => {
    this.setState({
      isHovered: false
    });
  };

  handleDeleteMessage = () => {
    const { subcampaignId, conversationExternalId, message, dispatchDeleteMessage } = this.props;

    dispatchDeleteMessage(subcampaignId, conversationExternalId, message);
  };

  renderAttachment() {
    const { message } = this.props;
    const attachment = message.attachments[0];
    const isImage = /image/.test(attachment.mimeType);

    if (attachment.status === 'uploading') {
      return (
        <div className="messages-user-message__attachment">
          <div className="messages-user-message__attachment-icon">
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          </div>
          <div>
            <div className="messages-user-message__attachment-title">
              {`${t('messenger.file_uploading_label')}:`}
            </div>
            {message.text}
          </div>
        </div>
      );
    }

    if (attachment.status === 'failed') {
      return (
        <div className="messages-user-message__attachment">
          <div className="messages-user-message__attachment-icon">
            <SVGIcon
              name="file-error"
              width={36}
              height={45}
            />
          </div>
          <div>
            <div className="messages-user-message__attachment-title">
              {`${t('messenger.file_failed_label')}:`}
            </div>
            {message.text}
          </div>
        </div>
      );
    }

    return (
      <React.Fragment>
        <a
          className="messages-user-message__attachment"
          href={attachment.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          <div className="messages-user-message__attachment-icon">
            <SVGIcon
              name={getAttachmentIcon(attachment)}
              width={36}
              height={45}
            />
          </div>
          <div>
            {parseBasicUrls(message.text, message.externalId)}
          </div>
        </a>
        {
          isImage &&
            <MediaPreview
              className="messages-user-message__media"
              attachment={{
                id: 'user-message-attachment',
                url: attachment.url,
                format: 'image'
              }}
              width={200}
              height={200}
            />
        }
      </React.Fragment>
    );
  }

  renderContextMenu(position: string): React.ReactNode | undefined | null {
    const { message, currentAccountUUID } = this.props;

    const isMine = message.senderId === currentAccountUUID && position === 'right';
    const isLessThan5MinutesOld = isLessThanNMinutesOld(message.createdAt, 5);

    const items = [
      ...insertIf(isMine && !message.isDeleted && isLessThan5MinutesOld && !isTempMessage(message), {
        name: 'delete-message',
        text: t('messenger.context_menu.delete_message_label'),
        onClick: this.handleDeleteMessage
      })
    ];

    if (items.length === 0) {
      return null;
    }

    return (
      <div className="messages-user-message__context-menu">
        <MoreButtonWithMenu width={20} contextMenuProps={{ items }} />
      </div>
    );
  }

  renderDeletedMessage(): React.ReactNode {
    return (
      <div className="messages-user-message__deleted-message">
        <SVGDynamicIcon
          name="cross-out-circle-custom-fg"
          width={24}
          height={24}
          customColor="grey"
        />
        <div className="messages-user-message__deleted-message-desc">
          <div className="messages-user-message__deleted-message--text">
            {t('messenger.deleted_message.text')}
          </div>
          <div className="messages-user-message__deleted-message-sidenote">
            {t('messenger.deleted_message.sidenote')}
          </div>
        </div>
      </div>
    );
  }

  renderMessage(message: MessengerMessage, hasAttachment: boolean): React.ReactNode {
    return (
      hasAttachment ? this.renderAttachment() : parseBasicUrls(message.text, message.externalId)
    );
  }

  render() {
    const { message, isBrand, subcampaignId } = this.props;
    const { isHovered } = this.state;
    const hasAttachment = message.attachments && message.attachments.length > 0;
    const sender = this.getSender();
    let senderName = '';

    if (!isNil(sender)) {
      senderName = truncate(`${sender.firstName} ${sender.lastName}`, { length: MAX_SENDER_NAME_LENGTH });
    }

    let position = 'right';

    if (
      (isBrand && message?.metadata?.profile?.type === MESSAGE_TYPE_INFLUENCER) ||
      (!isBrand && message?.metadata?.profile?.type === MESSAGE_TYPE_BRAND)
    ) {
      position = 'left';
    }

    const messageClass = classnames({
      'messages-user-message': true,
      [`messages-user-message--position-${position}`]: true,
      'messages-user-message--attachment-failed': hasAttachment && message.attachments[0].status === 'failed'
    });

    return (
      <div
        className={messageClass}
        data-id={message.externalId}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <div className="messages-user-message__avatar">
          <EntityAvatar
            bordered
            size="bigger"
            entity={this.getEntity()}
            openInNewTab
          />
        </div>
        <div className="messages-user-message__container" data-hj-suppress>
          <div
            className="messages-user-message__bubble text-preserve-whitespaces"
            data-qa="messages-user-message__bubble"
          >
            {
              message.isDeleted ?
                this.renderDeletedMessage() :
                this.renderMessage(message, hasAttachment)
            }
          </div>
          <div className="messages-user-message__footer">
            {`${senderName} ${getDisplayDateAndTime(message.createdAt)}`}
            {
              position === 'right' &&
                <MessageSeen
                  subcampaignId={subcampaignId}
                  message={message}
                />
            }
          </div>
        </div>
        {
          isHovered &&
            this.renderContextMenu(position)
        }
      </div>
    );
  }
}

const mapStateToProps = ({ messages, account }, props: OwnProps): StateProps => ({
  meta: messages.metaBySubcampaign[props.subcampaignId],
  conversationExternalId: messages.conversationId,
  currentAccountUUID: account.currentAccount ? account.currentAccount.uuid : null
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteMessage: (subcampaignId: number, conversationExternalId: string, message: MessengerMessage) =>
    dispatch(deleteMessage(subcampaignId, conversationExternalId, message))
});

export default connect(mapStateToProps, mapDispatchToProps)(UserMessage);
